import { useState } from 'react';
import { OrderListSearchParamsState } from '../context';
import { CompoundFilterState, CompoundFilterStateOption } from '@trukt/ui/CompoundFilter';
import {
  buildDateOption,
  DateFilterData,
} from '../components/sub-topic-options/components/date-filter';
import { ORDER_STATUS_MAP } from '@trukt/portal-utils/OrderStatusMap';
import {
  buildLocationRadiusOption,
  LocationRadiusFilterData,
} from '../components/sub-topic-options/components/location-filter';
import {
  buildContactOption,
  ContactFilterData,
} from '../components/sub-topic-options/components/SearchContactInput';
import {
  BranchFilterData,
  buildBranchOption,
} from '../components/sub-topic-options/components/SearchBranchInput';

export type OrderListCompoundFilterLiterals = keyof OrderListSearchParamsState;
export type OrderListCompoundFilterState = CompoundFilterState<OrderListCompoundFilterLiterals>;

export type OrderListFilterStatusOptions = CompoundFilterStateOption<unknown, ORDER_STATUS_MAP>[];
export type OrderListFilterContactOptions = CompoundFilterStateOption<ContactFilterData>[];
export type OrderListFilterDateOptions = CompoundFilterStateOption<DateFilterData>[];
export type OrderListFilterPickupOptions = CompoundFilterStateOption<
  LocationRadiusFilterData,
  string
>[];
export type OrderListFilterDeliveryOptions = CompoundFilterStateOption<
  LocationRadiusFilterData,
  string
>[];
export type OrderListFilterBranchOptions = CompoundFilterStateOption<BranchFilterData>[];

export function useOrderListCompoundFilter(
  optionParams: OrderListSearchParamsState,
  options: { statusTab?: ORDER_STATUS_MAP } = {},
) {
  const [state, setState] = useState<OrderListCompoundFilterState>(() =>
    buildInitialState(optionParams, options.statusTab),
  );

  return {
    state,
    setState,
  };
}

export function buildInitialState(
  optionsParams: OrderListSearchParamsState,
  statusTab: ORDER_STATUS_MAP = ORDER_STATUS_MAP.ALL,
  { activeFilters }: { activeFilters?: (keyof OrderListSearchParamsState)[] } = {},
): OrderListCompoundFilterState {
  return [
    {
      id: 'status',
      label: 'Status',
      options: [
        ORDER_STATUS_MAP.SUBMITTED,
        ORDER_STATUS_MAP.ON_HOLD,
        ORDER_STATUS_MAP.SCHEDULED,
        ORDER_STATUS_MAP.PICKED_UP,
        ORDER_STATUS_MAP.DELIVERED,
        ORDER_STATUS_MAP.CANCELED,
      ].map((status) => {
        const isInUrl = optionsParams.status.includes(status);
        const isActive =
          status.replace('_', ' ').toLowerCase() === statusTab.replace('_', ' ').toLowerCase();
        return {
          id: status,
          label: status,
          stateModifiers: {
            isRemovable: false,
            isActive: isActive || isInUrl,
            isClearable: !isActive,
            isToggleable: !isActive,
          },
        };
      }),
      stateModifiers: {
        isActive: activeFilters?.includes('status'),
      },
    },
    {
      id: 'contacts',
      label: 'Contacts',
      options: optionsParams.contacts?.map(buildContactOption) ?? [],
      stateModifiers: {
        isActive: activeFilters?.includes('contacts'),
      },
    },
    {
      id: 'dates',
      label: 'Dates',
      options: optionsParams.dates?.map(buildDateOption) ?? [],
      stateModifiers: {
        isActive: activeFilters?.includes('dates'),
      },
    },
    {
      id: 'pickup',
      label: 'Pickup',
      options: optionsParams.pickup?.map(buildLocationRadiusOption) ?? [],
      stateModifiers: {
        isActive: activeFilters?.includes('pickup'),
      },
    },
    {
      id: 'delivery',
      label: 'Delivery',
      options: optionsParams.delivery?.map(buildLocationRadiusOption) ?? [],
      stateModifiers: {
        isActive: activeFilters?.includes('delivery'),
      },
    },
    {
      id: 'branches',
      label: 'Branch',
      options: optionsParams.branches?.map(buildBranchOption),
      stateModifiers: {
        isActive: activeFilters?.includes('branches'),
      },
    },
  ];
}
